import React, { Suspense, useState, useCallback, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import DropZone from 'react-dropzone';
import Drawer from '../../Common/Drawer/Drawer';
import FileChooser from '../../Common/FileChooser';
import Icon from '../../Common/Icon';
import Notification from '../../Common/Notification';
import LoadingIndicator from '../../Common/LoadingIndicator/LoadingIndicator';
import ImportErrorRow from '../../Common/ImportErrorRow';
import Button from '../../Common/Button';
import { useImportBaseline, useDownloadImportTemplate } from '../../../apis/baselines';
import { ImportRowError } from '../../../apis/baselines/baseline.types';

// Error Fallback Component
const ErrorFallback: React.FC<{ error: unknown; resetErrorBoundary: () => void }> = ({
	error,
	resetErrorBoundary,
}) => (
	<div className="alert alert-danger m-3">
		<h5>Something went wrong loading the baseline import:</h5>
		<p>{error instanceof Error ? error.message : String(error)}</p>
		<button className="btn btn-secondary" onClick={resetErrorBoundary}>
			Try again
		</button>
	</div>
);

// Loading Component for Suspense
const LoadingComponent: React.FC = () => (
	<LoadingIndicator isLoading={true}>
		<div>Loading import...</div>
	</LoadingIndicator>
);

// Component Props
interface ComponentProps {
	subscriptionId: string;
	baselineId: string;
	onComplete: () => void;
	onCancel: () => void;
}

const BaselineImportDrawerContent: React.FC<ComponentProps> = ({ baselineId, onComplete, onCancel }) => {
	const [droppedFile, setDroppedFile] = useState<File | null>(null);
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [isFormValid, setIsFormValid] = useState<boolean>(true);
	const [importErrors, setImportErrors] = useState<ImportRowError[]>([]);
	const fileInputTargetRef = useRef<HTMLInputElement | null>(null);

	const importMutation = useImportBaseline((result) => {
		if (result.isError) {
			Notification('error', 'Error', result.errors?.[0]?.message ?? 'Failed to import baseline');
			return;
		}

		if (result.importErrors.length > 0) {
			Notification('error', 'Validation Errors', 'Please correct the errors in the Excel document and try again');
			setImportErrors(result.importErrors);
			setSelectedFile(null);
			setDroppedFile(null);

			// hack to clear the file out of the file input. Only present when the file was
			// chosen via the file picker; a drag-and-dropped file has no input target.
			if (fileInputTargetRef.current) {
				fileInputTargetRef.current.value = '';
			}
		} else {
			Notification('success', 'Import Successful', '');
			onComplete();
		}
	});

	const downloadTemplateMutation = useDownloadImportTemplate();

	const onFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			setSelectedFile(file);
			setDroppedFile(null);
			setIsFormValid(true);
			setImportErrors([]);
			fileInputTargetRef.current = e.target;
		}
	}, []);

	const onDrop = useCallback((files: File[]) => {
		const dropped = files && files.length > 0 ? files[0] : null;

		if (dropped) {
			if (dropped.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
				Notification('error', 'Invalid File Type', 'You must pick an Excel document with .xlsx extension');
			} else {
				setDroppedFile(dropped);
				setSelectedFile(null);
				setImportErrors([]);
			}
		}
	}, []);

	const onImportClick = useCallback(() => {
		const importFile = droppedFile ?? selectedFile;
		if (!importFile) {
			setIsFormValid(false);
			return;
		}

		setImportErrors([]);
		importMutation.mutate(
			{ baselineId: Number(baselineId), file: importFile },
			{
				onError: (error) => {
					Notification('error', 'Error', error.message);
				},
			}
		);
	}, [droppedFile, selectedFile, baselineId, importMutation]);

	const onDownload = useCallback(() => {
		downloadTemplateMutation.mutate('', {
			onError: (error) => {
				Notification('error', '', error.message);
			},
		});
	}, [downloadTemplateMutation]);

	return (
		<div>
			<Drawer isWide={true} title="Baseline Import" onCancelClick={onCancel} saveText="Import" onSaveClick={onImportClick}>
				<LoadingIndicator isLoading={importMutation.isPending}>
					<div className="row">
						<div className="col-1"><h1><span className="pacer-step-badge">1</span></h1></div>
						<div className="col-9"><div className="mt-3">Download the import tool Excel template</div></div>
						<div className="col-1"><div className="mt-1"><Button type="btn" bootstrapClass="primary text-end" onClick={onDownload} text="Download" /></div></div>
					</div>
					<div className="row mt-4">
						<div className="col-1"><h1><span className="pacer-step-badge">2</span></h1></div>
						<div className="col-11"><div className="mt-2">Add your tasks to the Excel template. Ensure all required fields are entered in for each task. If you copy/paste from another workbook, please use Paste Special &gt; Values.</div></div>
					</div>
					<div className="row mt-3">
						<div className="col-1"><h1 className="mt-2"><span className="pacer-step-badge">3</span></h1></div>
						<div className="col-11">
							<DropZone
								noClick={true}
								accept={{
									'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [], // Accepts .xlsx files
								}}
								multiple={false}
								onDrop={onDrop}
							>
								{({ getRootProps, getInputProps, isDragActive }) => (
									<div {...getRootProps({ className: `dropzone ${isDragActive ? 'active' : ''}` })}>
										<input {...getInputProps()} />
										{isDragActive ? (
											<div className="dropzone-overlay">
												<div className="overlay-content">
													<Icon name="cloud-upload-alt" />
													<p>Drop file here to import</p>
												</div>
											</div>
										) : (
											<p>Drag and drop a file here or select one below</p>
										)}
										<FileChooser
											name="file"
											onChange={onFileChange}
											isRequired
											errorMessage="A file is required"
											acceptTypes=".xlsx"
										/>
										{droppedFile && (
											<div className="dropped-file">File Name: {droppedFile.name}</div>
										)}
										{!isFormValid && (
											<div className="text-danger">
												You must select or drag and drop a valid excel file
											</div>
										)}
										{importErrors.length > 0 && (
											<div className="text-danger">
												{importErrors.length} error
												{importErrors.length === 1 ? '' : 's'} found during the import:
											</div>
										)}
										{importErrors.map((importError) => (
											<ImportErrorRow
												importError={importError}
												key={`${importError.rowNumber}-${importError.columnNumber}`}
											/>
										))}
									</div>
								)}
							</DropZone>
						</div>
					</div>
				</LoadingIndicator>
			</Drawer>
		</div>
	);
};

const BaselineImportDrawer: React.FC<ComponentProps> = (props) => {
	return (
		<ErrorBoundary FallbackComponent={ErrorFallback} onReset={() => window.location.reload()}>
			<Suspense fallback={<LoadingComponent />}>
				<BaselineImportDrawerContent {...props} />
			</Suspense>
		</ErrorBoundary>
	);
};

export default BaselineImportDrawer;
